import React, { useState } from "react";
import { loginWithEmail, verifyOTP } from "../api/auth";
import logo from "../assets/ft-logo.svg";

function Login({ setIsAuthenticated, setUser }) {
  const [step, setStep] = useState("email"); // "email" or "otp"
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [agreed, setAgreed] = useState(false);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();

    if (!agreed) {
      setError("Please accept the Terms of Service and Privacy Policy to continue");
      return;
    }

    setError("");
    setLoading(true);

    try {
      const response = await loginWithEmail(email);
      if (response.message) {
        setStep("otp");
      }
    } catch (err) {
      const fallback =
        err.response?.status === 429
          ? "Too many attempts. Please try again later."
          : "Failed to send OTP";
      setError(err.response?.data?.detail || fallback);
    } finally {
      setLoading(false);
    }
  };

  const handleOTPSubmit = async (e) => {
    e.preventDefault();

    if (!agreed) {
      setError("Please accept the Terms of Service and Privacy Policy to continue");
      return;
    }

    setError("");
    setLoading(true);

    try {
      const response = await verifyOTP(email, otp);
      if (response.access_token) {
        setIsAuthenticated(true);
        setUser(response.user);
      }
    } catch (err) {
      const fallback =
        err.response?.status === 429
          ? "Too many attempts. Please try again later."
          : "Invalid OTP";
      setError(err.response?.data?.detail || fallback);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f8f8fb] flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex justify-center mb-12">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white">
              <img
                src={logo}
                alt="FastTrackr Logo"
                className="h-10 w-10"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'block';
                }}
              />
              <div style={{display: 'none', color: '#259f66'}} className="text-xl font-bold">FT</div>
            </div>
            <div>
              <div className="text-xl font-semibold text-gray-900">FastTrackr</div>
              <div className="text-xs text-[#818898] font-medium">RIA Transition</div>
            </div>
          </div>
        </div>

        {/* Form Card */}
        <div className="bg-white border border-gray-100 rounded-2xl p-8 shadow-sm">
          <div className="mb-8 text-center">
            <h1 className="text-2xl font-semibold text-gray-900 mb-2">
              {step === "email" ? "Welcome back" : "Enter verification code"}
            </h1>
            <p className="text-sm text-[#718096]">
              {step === "email" 
                ? "Sign in to your account to continue" 
                : `We sent a code to ${email}`
              }
            </p>
          </div>

          {error && (
            <div className="mb-6 p-3 bg-red-50 border border-red-100 text-red-600 rounded-lg text-sm flex items-center gap-2">
              <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <span>{error}</span>
            </div>
          )}

          {step === "email" ? (
            <form onSubmit={handleEmailSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#259f66]/20 focus:border-[#259f66] transition-all outline-none text-gray-900 placeholder:text-[#818898]"
                  placeholder="name@company.com"
                  autoFocus
                />
              </div>
              <button
                type="submit"
                disabled={loading || !agreed}
                className="w-full py-3 bg-[#161f48] text-white rounded-lg font-medium hover:bg-[#272835] active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? "Sending..." : "Continue"}
              </button>
            </form>
          ) : (
            <form onSubmit={handleOTPSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Verification code
                </label>
                <input
                  type="text"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 4))}
                  required
                  maxLength={4}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#259f66]/20 focus:border-[#259f66] transition-all outline-none text-center text-2xl tracking-[0.3em] font-semibold text-gray-900 placeholder:text-gray-300"
                  placeholder="----"
                  autoFocus
                />
              </div>
              <button
                type="submit"
                disabled={loading || !agreed}
                className="w-full py-3 bg-[#161f48] text-white rounded-lg font-medium hover:bg-[#272835] active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? "Verifying..." : "Verify"}
              </button>
              <button
                type="button"
                onClick={() => setStep("email")}
                className="w-full py-2 text-sm text-[#818898] hover:text-gray-700 font-medium transition-colors"
              >
                ← Change email
              </button>
            </form>
          )}

          <label className="flex items-center gap-2.5 mt-3 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-emerald-500 focus:ring-2 focus:ring-emerald-500/30 cursor-pointer flex-shrink-0"
            />
            <span className="text-xs text-[#818898] leading-tight">
              You agree to our{" "}
              <a
                href="https://www.fasttrackr.ai/tos-transitions"
                target="_blank"
                rel="noopener noreferrer"
                className="text-emerald-600 underline hover:text-emerald-700"
                onClick={(e) => e.stopPropagation()}
              >
                Terms of Service
              </a>{" "}
              and{" "}
              <a
                href="https://www.fasttrackr.ai/privacy-policy-transitions"
                target="_blank"
                rel="noopener noreferrer"
                className="text-emerald-600 underline hover:text-emerald-700"
                onClick={(e) => e.stopPropagation()}
              >
                Privacy Policy
              </a>
            </span>
          </label>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-xs text-[#818898]">
            © 2026 FastTrackr. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
}

export default Login;
